import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { ExternalLink, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ConversationDrawer } from '@/components/ConversationDrawer';
import { formatPhone, samePhone } from '@/lib/phone';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';

// Conversa ao vivo do Playground oficial: o jeito mais honesto de provar a API é
// conversar com ela. O cliente manda "oi" do próprio celular para o número
// conectado — o que abre a janela de 24h e deixa todo o resto livre — e daqui
// responde, cita, reage e vê os ✓ mudarem, com os webhooks chegando ao lado.

export function PlaygroundLive({ businessPhone, testPhone, instanceId }: {
  businessPhone?: string; testPhone: string; instanceId?: string;
}) {
  const t = useT();
  const digits = (businessPhone ?? '').replace(/\D/g, '');
  const link = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(t('playground.live.hello'))}` : '';
  const [qr, setQr] = useState('');
  useEffect(() => {
    if (!link) { setQr(''); return; }
    QRCode.toDataURL(link, { width: 160, margin: 1 }).then(setQr).catch(() => setQr(''));
  }, [link]);
  const testDigits = testPhone.replace(/\D/g, '');
  // A conversa que JÁ existe com este contato: o wa_id da Meta pode vir sem o
  // 9 do celular brasileiro, e montar o JID do que foi digitado abria uma
  // conversa vazia ao lado da verdadeira.
  const { data: convs = [] } = useQuery({
    queryKey: ['conversations', ''],
    enabled: testDigits.length >= 8,
    queryFn: async () => ((await apiFetch('GET', '/conversations')).data as { data?: { chat_jid: string }[] })?.data ?? [],
  });
  const chatJid = useMemo(() => {
    if (testDigits.length < 8) return null;
    const existing = convs.find((c) => c.chat_jid.endsWith('@s.whatsapp.net') && samePhone(c.chat_jid.split('@')[0], testDigits));
    return existing?.chat_jid ?? `${testDigits}@s.whatsapp.net`;
  }, [convs, testDigits]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <MessageCircle className="mt-0.5 size-5 shrink-0 text-[#128C7E]" />
          <div>
            <h2 className="font-semibold">{t('playground.live.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('playground.live.subtitle')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {qr && <img src={qr} alt="" className="size-32 shrink-0 rounded-md border bg-white p-1" />}
          <ol className="flex flex-col gap-1.5 text-sm">
            <li>
              1. {t('playground.live.step1')}{' '}
              {digits ? <strong>{formatPhone(businessPhone ?? '')}</strong> : <em>{t('playground.live.noNumber')}</em>}
              {link && (
                <a href={link} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-primary underline">
                  {t('playground.live.openWhatsApp')} <ExternalLink className="size-3" />
                </a>
              )}
            </li>
            <li>2. {t('playground.live.step2')}</li>
            <li>3. {t('playground.live.step3')}</li>
          </ol>
        </div>

        {chatJid ? (
          <ConversationDrawer
            inline open onOpenChange={() => undefined}
            chatJid={chatJid} canSend official instanceId={instanceId || undefined}
          />
        ) : (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t('playground.live.needPhone')}</p>
        )}
      </CardContent>
    </Card>
  );
}
